import React, {useState, ReactNode} from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import {debounce, useTheme} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import {Input, SelectElem} from './inputs'
import {
  FilterConfig,
  ModelsFiltersType,
  ChangeVal,
  formatFilter,
  SortOptions,
  makeSorter,
} from '../../components/filters'

export type ChangeType = {
  search?: string
  start?: string
  end?: string
  sort?: string
}

type SearchBarProps = {
  searchLabel?: string
  searchOptions?: string[]
  initial?: FilterConfig
  sortOptions?: {[key: string]: string}
  sortLabel?: string
  onChange: ChangeVal
  onSort?: (options: SortOptions) => void
  onRemoveFilter: (field: keyof ModelsFiltersType, val: any) => void
  children?: ReactNode
}

type SearchBarWidgetProps = {
  initialSearch?: string
  initialSort?: SortOptions
  searchLabel?: string
  sortOptions?: {[key: string]: string}
  sortLabel?: string
  onChange: ChangeVal
}

const SearchBarWidget = ({
  initialSearch,
  initialSort,
  searchLabel,
  sortOptions,
  sortLabel,
  onChange,
}: SearchBarWidgetProps) => {
  const [search, setSearch] = useState<string | undefined>(initialSearch)
  const theme = useTheme()
  const sm = useMediaQuery(theme.breakpoints.down('md'))
  const xs = useMediaQuery(theme.breakpoints.down('sm'))

  const handleChange = (field: string, updater?: (val: any) => void) => (val: any) => {
    updater && updater(val)
    onChange(field, val)
  }

  const roundBorder = {
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: '50px',
    p: 1,
    pr: 3,
  }
  const noBorder = {
    '& .MuiOutlinedInput-notchedOutline': {border: 'none'},
    '& .MuiInputLabel-root': {left: 10},
  }

  const styling = {
    md: {
      border: roundBorder,
      search: noBorder,
      divider: {orientation: 'vertical'},
      filters: {
        container: {},
        button: {
          sx: {},
          variant: 'text',
        },
      },
      sort: {
        sx: {
          pb: 1,
          '& .MuiInput-root': {mt: '8px'},
          '& .MuiInputLabel-root': {top: -7},
        },
        variant: 'standard',
      },
    },
    sm: {
      border: {},
      search: {...noBorder, ...roundBorder, '& .MuiInputLabel-root': {left: 10, mt: 1}},
      divider: {orientation: 'horizontal'},
      filters: {
        container: roundBorder,
        button: {
          sx: {},
          variant: 'text',
        },
      },
      sort: {
        sx: {},
        variant: 'standard',
      },
    },
    xs: {
      border: {},
      search: {...noBorder, ...roundBorder, '& .MuiInputLabel-root': {left: 10, mt: 1}},
      divider: {orientation: 'horizontal'},
      filters: {
        container: {},
        button: {
          sx: roundBorder,
          variant: 'contained',
        },
      },
      sort: {
        sx: {...roundBorder, ...noBorder, '& .MuiFormLabel-root': {top: 10}},
      },
    },
  } as any
  let style = styling.md
  if (xs) {
    style = styling.xs
  } else if (sm) {
    style = styling.sm
  }

  return (
    <Box sx={style.border}>
      <Stack direction={{sm: 'column', md: 'row'}} spacing={3}>
        <Input
          label={searchLabel}
          value={search || ''}
          setter={debounce(handleChange('search', setSearch), 50)}
          sx={{mb: 2, flexGrow: 2, ...style.search}}
        />
        <Divider
          orientation={style.divider.orientation}
          flexItem
          sx={{m: 2, border: `1px solid ${theme.palette.primary.main}`}}
        />
        <Box sx={style.filters.container}>
          <Stack
            direction={{xs: 'column', sm: 'row'}}
            spacing={{xs: 2, sm: 3}}
            justifyContent="space-around"
            alignItems="center"
            sx={{height: '100%'}}
          >
            <Button
              sx={{width: '100%', ...style.filters.button.sx}}
              variant={style.filters.button.variant}
              onClick={() => handleChange('showFilters')(true)}
            >
              Filters
            </Button>

            {sortOptions && (
              <SelectElem
                sx={{
                  width: '100%',
                  ...style.sort.sx,
                }}
                value={initialSort?.key ? initialSort.key + (initialSort?.direction || '') : ''}
                variant={style.sort.variant}
                label={sortLabel || 'Ordered by'}
                values={sortOptions}
                onChange={handleChange('sort')}
              />
            )}
          </Stack>
        </Box>
      </Stack>
    </Box>
  )
}

type SelectedFiltersProps = {
  filters: ModelsFiltersType
  removeFilter: (field: keyof ModelsFiltersType, val: any) => void
}
const SelectedFilters = ({filters, removeFilter}: SelectedFiltersProps) => {
  const makeVal = (field: string, val: any) => {
    const [label, value] = formatFilter(field, val)
    return (
      <Chip
        key={label + '_' + value}
        label={
          <Typography>
            {label}: {value}
          </Typography>
        }
        variant="outlined"
        onClick={() => removeFilter(field as keyof ModelsFiltersType, val)}
        onDelete={() => removeFilter(field as keyof ModelsFiltersType, val)}
      />
    )
  }

  const items = Object.entries(filters).reduce((acc, [field, val]) => {
    if (Array.isArray(val)) {
      return [...acc, ...val.map((v) => makeVal(field, v))]
    }
    return [...acc, makeVal(field, val)]
  }, [] as ReactNode[])

  return <Box>{items}</Box>
}

const SearchBar = ({
  searchLabel,
  initial,
  sortOptions,
  sortLabel,
  onChange,
  onSort,
  onRemoveFilter,
  children,
}: SearchBarProps) => {
  const [showFilters, setShowFilters] = useState(false)

  const handleBarChange = (field: string, val: any) => {
    if (field === 'showFilters') {
      setShowFilters(!showFilters)
    } else if (field === 'sort' && onSort) {
      const [key, direction] = val.match(/(.*)(Asc|Desc)$/)?.slice(1, 3) || [null]
      if (key) {
        onSort({key, direction, sorter: makeSorter(key, direction)})
      } else {
        onSort({key: val, direction, sorter: (items: any[]) => items})
      }
    } else {
      onChange && onChange(field, val)
    }
  }

  return (
    <Stack direction="column" spacing={1}>
      <SearchBarWidget
        initialSearch={initial?.search}
        initialSort={initial?.sort}
        searchLabel={searchLabel}
        sortOptions={sortOptions}
        sortLabel={sortLabel}
        onChange={handleBarChange}
      />
      {initial?.filters && (
        <SelectedFilters filters={initial?.filters} removeFilter={onRemoveFilter} />
      )}
      {showFilters && children}
    </Stack>
  )
}

export default SearchBar
